import React, { useContext, useState, useRef, useEffect } from "react"
import ModalLayout from "./ModalLayout"
import {
  mapInputContainer,
  deliveryAddress,
  deliveryNotes,
  checkBoxContainer,
  mapContainer,
} from "./modal.module.css"
import { ModalContext } from "src/context/ModalContext"
import { Button } from "src/components/core/atoms/button"
import { CartContext } from "src/context/CartContext"
import GoogleMapEL from "src/components/core/atoms/googleMap"
import Geocode from "react-geocode"

import "./location.css"

export const AddLocation = ({ isDefault }) => {
  const { dispatchLocations, ACTIONS } = useContext(CartContext)
  const { closeModal } = useContext(ModalContext)
  const [error, setError] = useState({})
  const [query, setQuery] = useState({
    address: "",
    formattedAddress: "",
    fullAddress: {},
    coordinates: { lat: 51.500783012133304, lng: -0.12459635425652982 },
    zoom: 12,
    notes: '',
    isDefaultDropOff: false,
    isDefaultPickup: false,
  })

  let autoComplete

  const getFullAddress = addressObject => {
    let fullAddress = {}
    let counter = 0

    addressObject.address_components.forEach(addressLine => {
      counter++
      fullAddress[`address line ${counter}`] = addressLine.long_name
    })

    return fullAddress
  }

  const handlePlaceSelect = async () => {
    const addressObject = await autoComplete.getPlace() // get place from google api
    if (
      addressObject &&
      addressObject.geometry &&
      addressObject.geometry.location
    ) {
      const lat = await addressObject.geometry.location.lat()
      const lng = await addressObject.geometry.location.lng()
      const address = await addressObject.formatted_address
      const w3w = await window.what3words.api.convertTo3wa({ lat, lng })
      const currentZoom = query.zoom

      setQuery({
        ...query,
        address,
        formattedAddress: address,
        fullAddress: getFullAddress(addressObject),
        coordinates: {
          lat,
          lng,
        },
        W3WA: w3w.words,
        zoom: currentZoom > 19 ? currentZoom : 19,
      })
    }
  }

  const handleScriptLoad = React.useCallback(
    autoCompleteRef => {
      // assign autoComplete with Google maps place one time
      if (!autoComplete) {
        autoComplete = new window.google.maps.places.Autocomplete(
          autoCompleteRef.current
        )
        autoComplete.setFields([
          "address_components",
          "formatted_address",
          "geometry",
        ])
        autoComplete.addListener("place_changed", () => handlePlaceSelect())
      }
    },
    [autoComplete]
  )

  const handleChange = event => {
    const name = event.target.name
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value

    setQuery({
      ...query,
      [name]: value,
    })
  }

  const handleAdd = (location, isDefault) => {
    setError({
      ...error,
      submit_nickname: !location.nickname ? "Nickname is required." : "",
      submit_address: !location.address ? "Address is required." : "",
    })
    if (!location.nickname || !location.address) return

    dispatchLocations({
      type: ACTIONS.LOCATION_ADD,
      payload: { location, isDefault },
    })
    setQuery({
      ...query,
      formattedAddress: "",
      nickname: "",
    })
    closeModal()
  }

  const setCenter = async coordinates => {
    try {
      const currentZoom = query.zoom
      Geocode.setApiKey(process.env.GATSBY_GOOGLE_MAPS_API_KEY)
      Geocode.setLocationType("ROOFTOP")

      const newAddress = await Geocode.fromLatLng(
        query.coordinates.lat,
        query.coordinates.lng
      )

      const w3w = await window.what3words.api.convertTo3wa({ ...coordinates })

      setQuery({
        ...query,
        formattedAddress: newAddress.results[0].formatted_address,
        address: newAddress.results[0].formatted_address,
        fullAddress: getFullAddress(newAddress.results[0]),
        coordinates: { ...coordinates },
        W3WA: w3w.words,
        zoom: currentZoom > 17 ? currentZoom : 17,
      })
    } catch (error) {
      console.error( "geocode", { error: error } )
    }
  }

  const autoCompleteRef = useRef(autoComplete)

  useEffect(() => {
    handleScriptLoad(autoCompleteRef)
  }, [handleScriptLoad])

  useEffect(() => {
    setQuery({
      ...query,
      isDefaultDropOff: isDefault,
      isDefaultPickup: isDefault,
    })
  }, [isDefault])

  const W3w = ({ children }) => {
    const styles = {
      wrapper: {
        display: "inline-flex",
        flexDirection: "row",
        alignContent: "center",
        justifyContent: "center",
        gap: "5px",
        lineHeight: "21px",
      },
      logo: {
        fontFamily: "Arial",
        fontStyle: "normal",
        fontWeight: "bold",
        fontSize: "18px",
        color: "#E11F26",
      },
    }
    return (
      <div style={styles.wrapper}>
        <span style={styles.logo}>///</span>
        {children}
      </div>
    )
  }

  return (
    <ModalLayout>
      <div className={mapInputContainer}>
        <h1>Add location</h1>
        <div className={deliveryAddress}>
          <input
            ref={autoCompleteRef}
            type="search"
            name="formattedAddress"
            onChange={handleChange}
            placeholder="Search Address"
            value={query.formattedAddress}
            autoComplete="off"
            list="autocompleteOff"
          />
          <address>
            <W3w>{query.W3WA && `${query.W3WA}`}</W3w>
          </address>
          <input
            type="text"
            name="nickname"
            value={query.nickname || ""}
            onChange={handleChange}
            placeholder="Location Nickname"
          />
        </div>
        <div className={deliveryNotes}>
          <h2>
            Delivery Notes: <span>(Optional)</span>
          </h2>
          <textarea
            name="notes"
            placeholder="Additional information (optional)"
            value={query.notes}
            onChange={handleChange}
            maxLength={512}
          />
          <div className={checkBoxContainer}>
            <input
              type="checkbox"
              name="isDefaultDropOff"
              checked={query.isDefaultDropOff}
              onChange={handleChange}
            />
            <label htmlFor="isDefaultDropOff">
              Apply as default <b>drop-off</b> location to all services?
            </label>
          </div>
          <div className={checkBoxContainer}>
            <input
              type="checkbox"
              name="isDefaultPickup"
              checked={query.isDefaultPickup}
              onChange={handleChange}
            />
            <label htmlFor="isDefaultPickup">
              Apply as default <b>pickup</b> location to all services? -{" "}
            </label>
          </div>
        </div>
        <Button
          variant="solid"
          onClick={() => {
            handleAdd(
              {
                nickname: query.nickname,
                address: query.address,
                fullAddress: query.fullAddress,
                W3WA: query.W3WA,
                notes: query.notes,
                coordinates: { ...query.coordinates },
              },
              query.isDefaultDropOff,
              query.isDefaultPickup
            )
          }}
        >
          Add Location
        </Button>
        <label htmlFor="submit" name="error">
          {error.submit_address && error.submit_address}
        </label>
        <label htmlFor="submit" name="error">
          {error.submit_nickname && error.submit_nickname}
        </label>
      </div>
      <div className={mapContainer}>
        <GoogleMapEL
          center={query.coordinates}
          setCenter={setCenter}
          zoom={query.zoom}
        />
      </div>
    </ModalLayout>
  )
}

export default AddLocation
